import { apiErrorMessage } from '@agos/api-client'
import {
  getGetSuperAdminPlatformBillingQueryKey,
  useGetSuperAdminPlatformBilling,
  usePutSuperAdminPlatformBilling,
} from '@agos/api-client/platform'
import { T, formatCount, tt } from '@agos/shared'
import { useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { PageHeader, ReadFailedBlock, Spinner } from '@agos/ui'

import {
  SUPPLIER_FIELDS,
  autoEmailNote,
  blankIbanConsequence,
  fieldIsFaulty,
  ibanNote,
  ibanState,
  maySaveSupplier,
  numberingExample,
  platformBillingOf,
  squareIsEffective,
  squareNote,
  supplierDraftOf,
  BILLING_FLAGS,
  BILLING_NUMBERS,
  flagsOf,
  numbersOf,
  runFieldLabel,
  runPatch,
  supplierPatch,
  type BillingFlag,
  type BillingNumber,
  type SupplierDraft,
  type SupplierField,
} from './platform-billing'

/** The label each field carries, looked up at render so the extractor sees every literal. */
function fieldLabel(field: SupplierField): string {
  if (field === 'companyName') return tt('Názov firmy')
  if (field === 'address') return tt('Adresa')
  if (field === 'city') return tt('Mesto')
  if (field === 'zip') return tt('PSČ')
  if (field === 'country') return tt('Štát')
  if (field === 'ico') return tt('IČO')
  if (field === 'dic') return tt('DIČ')
  if (field === 'icDph') return tt('IČ DPH')
  if (field === 'iban') return 'IBAN'
  if (field === 'swift') return 'SWIFT / BIC'
  if (field === 'bankName') return tt('Banka')
  if (field === 'registrationNote') return tt('Zápis v registri')
  if (field === 'fromEmail') return tt('Odosielateľ - e-mail')
  if (field === 'fromName') return tt('Odosielateľ - meno')
  if (field === 'invoiceNumberPrefix') return tt('Predčíslie faktúr')
  if (field === 'footerText') return tt('Text v pätičke')
  if (field === 'logoUrl') return tt('Logo (URL)')
  if (field === 'accentColor') return tt('Farba')
  if (field === 'invoiceEmailSubject') return tt('Predmet e-mailu s faktúrou')
  return tt('Text e-mailu s faktúrou')
}

/**
 * The platform's own billing identity.
 *
 * **FB-S06.** PayBySquare is on and the IBAN is empty, so every invoice prints `IBAN -` and carries no
 * QR - the skip is correct and silent. A switch's state and its effect are two facts; this shows both.
 */
export function PlatformBillingPage() {
  const { data, isLoading, isError, refetch } = useGetSuperAdminPlatformBilling()
  const settings = useMemo(() => platformBillingOf(data), [data])
  const now = new Date()

  // ─── §3.14 - the write this page had been diagnosing without ────────────────────────────────────
  const queryClient = useQueryClient()
  /**
   * What the server refused, said out loud - `SAAS.md` §3.35.
   *
   * **This is the screen §3.30, §3.31, §3.33 and §3.34 have been guarding.** Its one mutation is
   * `PUT …/platform-billing`, which refuses a malformed IBAN, IČO, DIČ or IČ DPH, refuses a
   * mismatched IČ DPH/DIČ pair and writes an audit line - and none of those refusals could reach
   * the operator, on the screen that sets the account every AGOS invoice is paid into.
   *
   * `apiErrorMessage` is the shared reader - it takes `error` off the backend's `{error, code}`
   * envelope. The fallback covers a network drop, which carries no envelope to read.
   */
  const [refused, setRefused] = useState<string | null>(null)
  const refusedWith = (error: unknown) => {
    setRefused(apiErrorMessage(error, tt('Zmenu sa nepodarilo uložiť - server neodpovedal.')))
  }

  const save = usePutSuperAdminPlatformBilling({
    mutation: {
      onSuccess: () => {
        setEdited(null)
        // §3.114 - the run controls share this mutation, so they have to be dropped here too or a
        // saved change keeps reporting itself as unsaved until the page is reloaded.
        setFlagEdits(null)
        setNumberEdits(null)
        void queryClient.invalidateQueries({
          queryKey: getGetSuperAdminPlatformBillingQueryKey(),
        })
        setRefused(null)
      },
      onError: refusedWith,
    },
  })
  // Derived rather than seeded in an effect - the settings arrive after the first render, and null
  // also lets the save button tell *untouched* from *changed back*.
  const [edited, setEdited] = useState<SupplierDraft | null>(null)
  const stored = useMemo(() => supplierDraftOf(settings), [settings])
  const draft = edited ?? stored
  /* §3.114 - the billing run's own five controls, held apart from the supplier draft because they
   * save separately: typing a company address and turning the invoice run on are different decisions
   * and one of them starts sending mail. Both seeded from the response, both `null` until touched so
   * *unchanged* and *changed back* are distinguishable. */
  const [flagEdits, setFlagEdits] = useState<Record<BillingFlag, boolean> | null>(null)
  const [numberEdits, setNumberEdits] = useState<Record<BillingNumber, string> | null>(null)
  const flags = flagEdits ?? flagsOf(settings)
  const numbers = numberEdits ?? numbersOf(settings)
  const setFlags = setFlagEdits
  const setNumbers = setNumberEdits
  const runBody = runPatch(flags, numbers, settings)

  // §3.82 - **empty boxes are a claim.** This form read only `data`, so a load in flight and a
  // refused load both drew every field blank, which on a settings screen says *the platform has no
  // billing details* rather than *we have not been told yet*. `refused` below is the mutation's
  // banner and does not cover the read.
  //
  // §3.169, FB-920 - **and for two months that comment described a fix that was not there.** `isError`
  // gated the banner and nothing under it, so a refused read drew a blank company name with `IČO` and
  // `DIČ` and nothing after them - the platform's own statutory identity, presented as its state -
  // plus `data-iban-state="missing"`, the bank section **amber**, and *Bez IBAN-u faktúry neuvádzajú,
  // kam zaplatiť*. **A fabricated compliance warning about the platform's own bank details**, which is
  // FB-818's inversion: this class usually invents an all-clear, and an invented problem costs
  // somebody an investigation. And *Bez kľúča sa faktúry nedajú odoslať e-mailom*, a third claim.
  //
  // Returning early rather than gating each section, which is `FranchiseManagerSettingsPage`'s answer
  // to the same shape in the same app: *"Prázdny formulár by tvrdil, že nič nie je nastavené - preto
  // sa nezobrazuje."* Six ungated sections are six chances to miss one; there is nothing to show.
  //
  // The banner keeps its `platform-billing-error` marker, which is what §3.168's case selects by.
  if (isLoading || isError) {
    return (
      <main className="space-y-3" data-platform-billing>
        <PageHeader
          title={tt('Platforma')}
          description={tt('Fakturačné údaje platformy a jej vlastné náklady.')}
        />
        {isError ? (
          /* §3.171 - **the one Tower banner that replaces a form, so the one that is a titled block.**
           * §3.167 declined this judgement and `TODO.md` carried it; measured, 21 of the Tower's 22
           * `ReadFailed` sites sit beside a surface that loaded and **this is the only one that returns
           * early and draws the banner in place of the screen** - six sections of the platform's own
           * statutory billing identity. There is nothing else here to look at and there was no way out,
           * so the operator's only move was to reload the browser. The marker moves with the shape
           * (`data-platform-billing-error` to `data-platform-billing-failed`) and
           * `tower-read-failures.spec.ts` changes in the same commit, which is the cost §3.167 named. */
          <ReadFailedBlock
            marker="platform-billing"
            title={tt('Fakturačné údaje platformy sa nepodarilo načítať')}
            onRetry={() => void refetch()}
          >
            <T>Prázdny formulár by tvrdil, že nič nie je nastavené - preto sa nezobrazuje.</T>
          </ReadFailedBlock>
        ) : (
          <div className="py-10 flex justify-center" data-platform-billing-loading>
            <Spinner />
          </div>
        )}
      </main>
    )
  }

  return (
    <main className="space-y-3" data-platform-billing>
      {/* §3.78 - the title block every other screen in the product opens with. This page had
        * none at all: you landed on it and nothing on the screen said what it was. */}
      <PageHeader title={tt('Platforma')} description={tt('Fakturačné údaje platformy a jej vlastné náklady.')} />

      {refused !== null && (
        <p
          className="rounded-2xl border border-red-500/40 bg-red-500/5 p-3 text-xs text-red-500"
          data-platform-billing-refused
          role="alert"
        >
          {refused}
        </p>
      )}
      <section className="rounded-2xl border border-border p-3 space-y-1" data-billing-identity>
        <p className="text-sm font-bold">{settings.companyName}</p>
        <p className="text-[11px] text-muted-foreground">
          {/* Statutory identifiers, not prose - the same three abbreviations on every Slovak invoice. */}
          {tt('IČO')} {settings.ico} &middot; {tt('DIČ')} {settings.dic} &middot; {settings.icDph}
        </p>
      </section>

      <section
        className={`rounded-2xl border p-3 space-y-1 ${ibanState(settings) === 'ok' ? 'border-border' : 'border-amber-500/40'}`}
        data-billing-bank
        data-iban-state={ibanState(settings)}
      >
        <p className="text-xs">
          {settings.bankName} &middot; {settings.swift} &middot;{' '}
          <span data-billing-iban>{settings.iban === '' ? '-' : settings.iban}</span>
        </p>
        {ibanNote(settings) !== '' && (
          <p className="text-[11px] text-amber-500" data-iban-note>
            {ibanNote(settings)}
          </p>
        )}
        {/* §3.14 - what the warning above actually costs. The page named the fault and not the
          * consequence, and the consequence is what makes it urgent: the run is enabled. */}
        {blankIbanConsequence(settings) !== '' && (
          <p className="text-[11px] font-semibold text-amber-500" data-iban-consequence>
            {blankIbanConsequence(settings)}
          </p>
        )}
        <p className="text-xs" data-square-state={squareIsEffective(settings) ? 'effective' : 'ineffective'}>
          PayBySquare: {settings.paymentSquareEnabled ? <T>zapnutý</T> : <T>vypnutý</T>}
        </p>
        {squareNote(settings) !== '' && (
          <p className="text-[11px] text-amber-500" data-square-note>
            {squareNote(settings)}
          </p>
        )}
      </section>

      <section className="rounded-2xl border border-border p-3 space-y-1" data-billing-run>
        <p className="text-xs" data-auto-billing={settings.autoBillingEnabled ? 'on' : 'off'}>
          <T>Automatická fakturácia</T>: {settings.autoBillingEnabled ? <T>zapnutá</T> : <T>vypnutá</T>}
        </p>
        <p className="text-xs" data-auto-email={settings.autoEmailInvoices ? 'on' : 'off'}>
          <T>Automatické odosielanie</T>: {settings.autoEmailInvoices ? <T>zapnuté</T> : <T>vypnuté</T>}
        </p>
        {autoEmailNote(settings) !== '' && (
          <p className="text-[11px] text-amber-500" data-auto-email-note>
            {autoEmailNote(settings)}
          </p>
        )}
        <p className="text-[11px] text-muted-foreground" data-numbering>
          <T>Ďalšia faktúra</T>: {numberingExample(settings, now)} &middot; <T>splatnosť</T>{' '}
          {formatCount(settings.defaultDueDays)} <T>dní</T> &middot; DPH{' '}
          {formatCount(settings.defaultVatRatePct)} %
        </p>

        {/* §3.114 - **the controls for everything above.** All five were displayed and none was
          * editable: the two flags that decide whether the platform invoices at all and whether it
          * mails what it raises, the PayBySquare switch, the due-day count and the VAT rate. Legacy
          * has all five, and `PlatformBillingSettingsService.update` has accepted all five since it
          * was written - `agos-the-server-accepts-more-than-the-screen-offers`, and FB-505's shape a
          * third time on this one page. */}
        <div className="flex flex-wrap gap-2 pt-2" data-run-controls>
          {BILLING_FLAGS.map((flag) => (
            <button
              key={flag}
              type="button"
              data-run-flag={flag}
              data-run-flag-on={flags[flag] ? 'yes' : 'no'}
              aria-pressed={flags[flag]}
              onClick={() => setFlags({ ...flags, [flag]: !flags[flag] })}
              className={`rounded-lg border px-2 py-1 text-[11px] font-semibold ${
                flags[flag] ? 'border-primary/40 bg-primary/10 text-primary' : 'border-border text-muted-foreground'
              }`}
            >
              {runFieldLabel(flag)}: {flags[flag] ? tt('áno') : tt('nie')}
            </button>
          ))}
          {BILLING_NUMBERS.map((key) => (
            <label key={key} className="text-[10px]">
              <span className="text-muted-foreground">{runFieldLabel(key)}</span>
              <input
                inputMode="decimal"
                data-run-number={key}
                value={numbers[key]}
                onChange={(event) => setNumbers({ ...numbers, [key]: event.target.value })}
                className="block min-h-[32px] w-28 rounded-lg border border-border bg-background px-2 text-xs"
              />
            </label>
          ))}
          <button
            type="button"
            data-run-save
            disabled={Object.keys(runBody).length === 0 || save.isPending}
            onClick={() => save.mutate({ data: runBody })}
            className="self-end rounded-lg border border-primary/40 bg-primary/10 px-3 py-1.5 text-[11px] font-semibold text-primary disabled:opacity-40"
          >
            <T>Uložiť beh fakturácie</T>
          </button>
        </div>
        {/* Said, because turning the run on is the one change on this page that starts sending mail to
          * the owner's own customers. A count with no subject is not a confirmation. */}
        <p className="text-[10px] text-muted-foreground" data-run-dirty>
          {Object.keys(runBody).length === 0
            ? tt('Žiadne neuložené zmeny behu')
            : `${formatCount(Object.keys(runBody).length)} ${tt('neuložených zmien behu')}`}
        </p>
      </section>

      {/* ─── §3.14 - the form FB-505's closing measurement found missing ──────────────────────────
        *
        * `PUT …/platform-billing` was the last unaccounted write in the platform area, and the page
        * above it already computed `ibanState`, drew an amber border and printed `ibanNote` - with
        * nothing to set it. Measured: the owner's `iban` is empty while the billing run is enabled
        * and mails what it raises. */}
      <section className="rounded-2xl border border-border p-3 space-y-2" data-supplier-form>
        <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
          <T>Dodávateľ - údaje na faktúrach</T>
        </p>
        <div className="flex flex-wrap gap-2">
          {SUPPLIER_FIELDS.map((field) => (
            <label key={field} className="text-[10px]">
              <span className="text-muted-foreground">{fieldLabel(field)}</span>
              <input
                data-supplier-field={field}
                value={draft[field]}
                onChange={(event) => setEdited({ ...draft, [field]: event.target.value })}
                // The field's own validity comes from the same `ibanState` the banner reads, so the
                // two cannot disagree about whether an account number is acceptable.
                aria-invalid={fieldIsFaulty(field, draft) || undefined}
                className={`block min-h-[32px] w-44 rounded-lg border bg-background px-2 text-xs ${
                  fieldIsFaulty(field, draft) ? 'border-amber-500/60' : 'border-border'
                }`}
              />
            </label>
          ))}
          {/* Write-only. The server answers `resendApiKeySet` - a boolean, never a masked string -
            * so there is nothing to pre-fill and an empty box means *leave it alone*. */}
          <label className="text-[10px]">
            <span className="text-muted-foreground">
              <T>Resend API kľúč</T>
            </span>
            <input
              type="password"
              autoComplete="off"
              data-supplier-field="resendApiKey"
              placeholder={settings.resendApiKeySet ? tt('nastavený - nechať tak') : tt('nie je nastavený')}
              value={draft.resendApiKey}
              onChange={(event) => setEdited({ ...draft, resendApiKey: event.target.value })}
              className="block min-h-[32px] w-44 rounded-lg border border-border bg-background px-2 text-xs"
            />
          </label>
        </div>
        <p className="text-[10px] text-muted-foreground" data-supplier-key-note>
          {settings.resendApiKeySet
            ? tt('Kľúč je nastavený. Prázdne pole ho nechá ako je - vymazať ho odtiaľto nejde.')
            : tt('Bez kľúča sa faktúry nedajú odoslať e-mailom.')}
        </p>
        <button
          type="button"
          data-supplier-save
          disabled={!maySaveSupplier(draft, stored) || save.isPending}
          onClick={() => save.mutate({ data: supplierPatch(draft, stored) })}
          className="px-3 py-1.5 rounded-xl border border-primary/40 bg-primary/10 text-primary text-xs font-bold disabled:opacity-40"
        >
          <T>Uložiť</T>
        </button>
      </section>
    </main>
  )
}
